export type LegalSectionBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "table"; rows: string[][] };

export type LegalDocument = {
  title: string;
  subtitle: string;
  metadata: string[][];
  notice?: string;
  sections: Array<{
    heading: string;
    blocks: LegalSectionBlock[];
  }>;
};

export const privacyDocumentV1: LegalDocument = {
  title: "Truth-Seeker 隐私政策",
  subtitle: "更新日期：2026年9月11日 | 生效日期：2026年9月11日",
  metadata: [
    ["个人信息处理者", "致真精密仪器（青岛）有限公司"],
    ["统一社会信用代码", "91370212MA3Q9KK07F"],
    ["注册地址", "山东省青岛市崂山区株洲路169号昌盛产业园E座2001"],
    ["隐私联系渠道", "0532-89267428（公司总机）"],
    ["个人信息保护联系渠道", "致真精密仪器（青岛）有限公司，0532-89267428（公司总机）"],
    ["版本号", "V1.0"]
  ],
  sections: [
    {
      heading: "一、适用范围",
      blocks: [
        { type: "paragraph", text: "本政策适用于用户使用 Truth-Seeker 科研助手及相关服务过程中涉及的个人信息、科研资料、课题组资料、模型调用数据、系统运行日志和安全审计数据处理活动。" },
        { type: "paragraph", text: "如机构版、私有化部署版本、仪器联动版本或特定客户项目另有数据处理协议、安全协议、合同附件或机构管理规则，相关约定可与本政策共同适用；不一致处，以更能保护用户权益、机构合规要求或双方签署文件的约定为准。" }
      ]
    },
    {
      heading: "二、基本原则",
      blocks: [{
        type: "list",
        items: [
          "用户控制：用户可决定资料上传、共享、导出、删除和授权范围。",
          "默认私有：个人空间默认仅本人可见；课题组空间按角色和权限共享。",
          "最小必要：仅为完成用户任务、保障服务和满足合规要求处理必要数据。",
          "不默认训练：用户私有资料不会默认用于训练公开模型或第三方模型。",
          "分级授权：个人、PI/导师、管理员、成员、访客等角色按权限访问。",
          "过程可追溯：关键访问、权限变更、第三方调用、异常行为和系统故障保留必要日志。"
        ]
      }]
    },
    {
      heading: "三、我们可能处理的数据",
      blocks: [{
        type: "table",
        rows: [
          ["数据类别", "示例"],
          ["账号与身份信息", "姓名、手机号/邮箱、所属机构、课题组、角色、账号状态、登录凭证和管理员配置。"],
          ["用户上传资料", "论文 PDF、题录、DOI、BibTeX、笔记、批注、项目材料、基金材料、实验记录、仪器参数、图像、报告草稿等。"],
          ["科研过程数据", "会话、问题、检索记录、引用核验结果、证据表、知识库条目、写作草稿、历史任务、任务日志和操作记录。"],
          ["个性化配置", "研究方向、常用模板、引用格式、常用数据源、偏好设置、任务习惯和界面配置。"],
          ["课题组与权限数据", "项目空间、成员列表、角色权限、资料交接记录、共享范围、下载/导出记录和管理员操作。"],
          ["系统与安全日志", "登录时间、设备信息、IP/网络信息、接口调用、错误日志、异常行为、权限变更和安全审计记录。"]
        ]
      }]
    },
    {
      heading: "四、数据用途",
      blocks: [{
        type: "list",
        items: [
          "提供文献检索、论文阅读、摘要生成、引用核验、证据回链、综述辅助和科研写作草稿。",
          "提供项目书、组会材料、开题材料、调研报告、实验报告、论文 Discussion 等成果输出辅助。",
          "帮助用户沉淀个人文献库、实验资料库、历史输出库和可复用科研资料空间。",
          "在授权范围内支持课题组资料共享、权限管理、成员协作、资料交接和知识沉淀。",
          "支持多端同步，使用户能连续使用同一套资料、任务、偏好和历史记录。",
          "排查故障、保障系统安全、记录必要审计日志、处理投诉和争议。",
          "在用户明确授权、机构合同约定或法律法规允许范围内，用于产品统计分析、功能改进和服务质量评估。"
        ]
      }]
    },
    {
      heading: "五、AI 模型、第三方服务与跨境处理",
      blocks: [
        { type: "paragraph", text: "Truth-Seeker 可能调用大模型服务、文献数据库、开放学术数据源、搜索 API、机构授权数据库、仪器接口或其他第三方服务，以完成文献检索、摘要生成、引用核验、写作辅助、实验报告生成和仪器数据联动。" },
        { type: "paragraph", text: "我们会控制传输范围，只向第三方服务传递完成当前任务所必需的信息，并尽量减少与任务无关的信息传输。涉及机构数据库、付费数据库、学校图书馆资源或第三方 API 时，将按对应授权范围、服务条款和访问限制调用，不绕过付费墙，不批量分发版权全文。" },
        { type: "paragraph", text: "当前版本默认不向境外传输用户私有科研资料。若后续接入境外模型、境外学术数据库或其他境外处理场景，将在产品或协议中另行告知处理目的、数据范围、境外接收方、保存期限和用户权利，并按适用法律法规完成必要评估、认证、标准合同或单独授权。当涉及数据出境时，将严格按照《个人信息保护法》等要求，完成安全评估、标准合同备案或认证等法定程序，依法取得相应许可或备案。" },
        { type: "paragraph", text: "用户上传的私有论文、实验数据、课题组资料、未公开研究内容和机构资料，不会默认用于训练公开模型或第三方模型。" }
      ]
    },
    {
      heading: "六、个人空间、课题组空间与资料交接",
      blocks: [
        { type: "paragraph", text: "个人空间资料默认仅用户本人可见，包括个人上传资料、笔记、会话、草稿、历史输出和个人偏好。" },
        { type: "paragraph", text: "课题组空间按照 PI/导师、管理员、成员、访客等角色和权限访问。不同角色可拥有不同的查看、上传、编辑、下载、分享、删除、成员管理和资料交接权限。" },
        { type: "paragraph", text: "学生毕业、成员离组、项目交接或课题组资料迁移时，应由课题组负责人、管理员或机构授权代表确认资料归属、可见范围和权限变更。系统可记录关键权限变更和资料访问行为，用于追踪异常访问、误操作和资料流转风险。" }
      ]
    },
    {
      heading: "七、保存期限、删除和响应机制",
      blocks: [
        { type: "paragraph", text: "账号与身份信息通常保存至账号注销、服务终止或机构合同到期后必要期限；法律法规、合同或审计要求另有规定的除外。" },
        { type: "paragraph", text: "用户上传资料、生成内容和历史任务一般保存至用户主动删除、项目空间删除、账号注销、机构管理员处理或服务合同终止。" },
        { type: "paragraph", text: "系统运行与安全日志通常会保留必要期限用于安全审计、故障排查和争议处理；原则上不少于 6 个月，具体以产品公告、机构合同或法律要求为准。" },
        { type: "paragraph", text: "备份数据会随备份周期滚动覆盖。用户删除或注销后，在线业务数据将按产品规则处理；备份、审计日志或依法需保留的数据，可能在必要期限内继续保存，但会限制访问和使用。" },
        { type: "paragraph", text: "用户可以申请查看、更正、导出、删除个人资料和历史记录，也可以撤销部分授权或关闭个性化偏好。对于您合理的请求，我们原则上将在15个工作日内予以响应。如遇情况复杂、涉及多方权益或需要机构管理员确认的情形，我们可能会适当延长处理期限，但会及时告知您原因和预计处理时间。" }
      ]
    },
    {
      heading: "八、敏感科研资料与机构合规提醒",
      blocks: [
        { type: "paragraph", text: "未发表论文、未公开基金材料、专利交底书、商业合作资料、实验原始数据、未公开样品数据、涉合作方数据和受保密协议限制的数据，建议按敏感科研资料处理。" },
        { type: "paragraph", text: "涉及国家秘密、工作秘密、商业秘密、个人敏感信息、伦理审查材料、临床或人体相关数据、机构明确限制上传的数据，用户应先取得所在机构、课题负责人、项目负责人、合作方或权利人的授权，并按要求进行脱敏、分级和权限控制。" }
      ]
    },
    {
      heading: "九、数据安全措施",
      blocks: [{
        type: "list",
        items: [
          "访问控制：基于账号、角色、项目空间和课题组权限控制数据访问。",
          "传输与存储保护：采用合理的传输加密、存储隔离、备份和访问控制措施。",
          "密钥与凭证保护：第三方 API Key、数据库凭证和系统密钥由后端统一管理，不在前端明文暴露。",
          "日志与审计：记录必要的登录、调用、权限变更、异常和安全事件日志。",
          "最小化传输：对第三方服务调用尽量减少原文、敏感字段和无关资料传输。",
          "权限回收：在成员离组、合同终止、账号注销或管理员调整权限时，支持权限变更和资料交接。"
        ]
      }]
    },
    {
      heading: "十、用户权利",
      blocks: [
        { type: "paragraph", text: "在法律法规允许范围内，用户可以申请访问、复制、更正、补充、删除个人信息，撤回授权，注销账号，导出资料，限制或拒绝部分处理，解释自动化处理规则，以及投诉或咨询个人信息处理问题。" },
        { type: "paragraph", text: "为保障账号和资料安全，我们可能要求用户进行身份验证。机构版或课题组空间中涉及多人权益、机构资料、项目资料或合同义务的请求，可能需要机构管理员、PI/导师或项目负责人确认。" }
      ]
    },
    {
      heading: "十一、未成年人",
      blocks: [{ type: "paragraph", text: "Truth-Seeker 主要面向高校、科研院所、企业研发部门和成年科研用户。未成年人使用本服务，应在监护人或学校、机构授权和指导下进行。涉及未成年人个人信息或科研活动数据的，应取得必要授权并遵守相关法律法规。" }]
    },
    {
      heading: "十二、生成式 AI 服务、内容标识与算法信息",
      blocks: [
        { type: "paragraph", text: "Truth-Seeker 如在中国境内面向公众提供生成式人工智能服务，将按照适用法律法规和监管要求履行算法备案、生成式人工智能服务备案、模型信息公示、生成合成内容标识、投诉举报、违法内容处置等义务。我们将在产品显著位置或相关页面提供模型/服务说明、内容标识方式和投诉渠道。" },
        { type: "paragraph", text: "我们将依据相关法规要求，对AI生成的文本、图表等内容添加必要的显式与隐式标识，以履行内容标识义务。" },
        { type: "paragraph", text: "AI 生成内容可能存在错误、遗漏、过时、引用不准确或推理不完整。正式投稿、项目申报、专利提交、对外发布或商业决策前，用户应自行核验关键事实、引用来源、数据结论和合规要求。" }
      ]
    },
    {
      heading: "十三、法律披露与安全事件处理",
      blocks: [
        { type: "paragraph", text: "如依据法律法规、监管要求、司法机关或行政机关依法提出的请求，我们可能依法披露必要信息，并尽量在法律允许范围内记录披露情况、控制披露范围和通知相关用户或机构。" },
        { type: "paragraph", text: "如发生个人信息安全事件或科研资料安全风险，我们将按照法律法规和内部安全流程采取补救措施，并在必要时通过产品通知、邮件、电话或机构管理员通知等方式告知受影响用户。" }
      ]
    },
    {
      heading: "十四、政策更新",
      blocks: [{ type: "paragraph", text: "我们可能根据产品功能、法律法规、监管要求、第三方服务、部署方式和数据处理方式变化更新本政策。重大变更将通过产品公告、站内通知、邮件或其他合理方式提示。用户继续使用服务，视为接受更新后的政策；如不同意更新，可停止使用并申请导出或删除相关数据。" }]
    },
    {
      heading: "十五、联系我们",
      blocks: [{ type: "paragraph", text: "个人信息处理者：致真精密仪器（青岛）有限公司。统一社会信用代码：91370212MA3Q9KK07F。注册地址：山东省青岛市崂山区株洲路169号昌盛产业园E座2001。隐私与数据安全相关问题可联系：0532-89267428（公司总机）；个人信息保护联系渠道：致真精密仪器（青岛）有限公司，0532-89267428（公司总机）。" }]
    }
  ]
};

export const termsDocumentV1: LegalDocument = {
  title: "Truth-Seeker 用户协议",
  subtitle: "更新日期：2026年9月11日 | 生效日期：2026年9月11日",
  metadata: [
    ["服务提供者", "致真精密仪器（青岛）有限公司"],
    ["统一社会信用代码", "91370212MA3Q9KK07F"],
    ["注册地址", "山东省青岛市崂山区株洲路169号昌盛产业园E座2001"],
    ["联系渠道", "0532-89267428（公司总机）"],
    ["版本号", "V1.0"]
  ],
  sections: [
    {
      heading: "一、接受协议与适用范围",
      blocks: [
        { type: "paragraph", text: "本协议是用户与服务提供者之间关于注册、登录和使用 Truth-Seeker 科研助手及相关服务的约定。用户点击“已阅读并同意《Truth-Seeker 用户协议》和《Truth-Seeker 隐私政策》”、注册账号、登录或实际使用服务，即表示已阅读、理解并同意本协议。本协议构成双方之间具有法律约束力的服务协议。" },
        { type: "paragraph", text: "Truth-Seeker 当前定位为面向科研人员、学生、课题组和机构客户的 AI 科研辅助产品，可能包括网页端、桌面端、机构版、私有化部署版本以及后续移动端。不同版本、套餐或部署方式对应的功能范围、数据处理方式和服务级别，可能以订单、机构合同、产品页面或补充协议进一步约定。" }
      ]
    },
    {
      heading: "二、账号注册与使用管理",
      blocks: [{
        type: "list",
        items: [
          "用户应提供真实、准确、完整的注册信息，并在信息变化后及时更新。机构用户还应遵守所在单位、课题组或管理员设置的账号和权限规则。我们将依据《互联网信息服务深度合成管理规定》等法规要求，对用户进行真实身份信息认证。未完成认证的，可能无法使用信息发布等相关功能。",
          "账号仅限注册用户本人或经机构授权的成员使用。用户应妥善保管账号、密码、验证码、API Key、机构授权凭证等访问凭据，不得出租、出借、转让或与无关人员共享。",
          "如发现账号被盗用、异常登录、资料被异常访问或权限被误配置，用户应及时通过本协议列明的联系渠道通知我们；我们可根据风险情况采取验证身份、限制访问、暂停服务或调整权限等措施。"
        ]
      }]
    },
    {
      heading: "三、服务内容与使用边界",
      blocks: [
        { type: "paragraph", text: "Truth-Seeker 可提供文献检索、PDF 阅读、摘要和综述辅助、引用核验、证据回链、科研写作草稿、实验报告辅助、资料沉淀、课题组共享、多端同步、仪器数据联动等能力。具体功能以实际开放版本为准。" },
        { type: "paragraph", text: "Truth-Seeker 的 AI 输出用于科研辅助和效率提升，不等同于论文审稿、法律意见、专利代理意见、医学诊断、财务建议或最终科研结论。正式投稿、项目申报、专利提交、成果发布、商业决策或对外承诺前，用户应自行核验事实、数据、引用、图表、实验结论和合规要求。用户应遵守科研诚信和科技伦理规范，不得利用本服务生成虚假实验数据、伪造研究成果或从事其他违反学术规范的行为。" },
        { type: "paragraph", text: "我们将依据相关法规要求，对AI生成的内容添加必要的显式与隐式标识，以履行内容标识义务。" }
      ]
    },
    {
      heading: "四、用户上传内容与合法授权",
      blocks: [
        { type: "paragraph", text: "用户上传论文 PDF、题录、笔记、批注、实验记录、仪器参数、图像、项目书、基金材料、专利材料、报告草稿、课题组资料或其他内容时，应确保对相关资料拥有合法使用权，或已取得权利人、课题负责人、所在机构、项目合作方或数据来源方的必要授权。" },
        {
          type: "list",
          items: [
            "不得上传、处理或传播侵犯他人知识产权、商业秘密、个人信息权益或其他合法权益的内容。",
            "不得上传国家秘密、工作秘密、未获授权的商业秘密、受保密协议限制的数据、机构明确禁止外传的数据，或法律法规禁止上传和处理的内容。",
            "涉及未发表论文、未公开基金材料、专利交底书、合作项目数据、实验原始数据和可识别个人信息的，应按敏感科研资料处理，并遵守所在机构的审批、脱敏和权限管理要求。",
            "如因用户上传或使用内容导致第三方索赔、监管调查、机构问责或其他争议，用户应配合核查并承担相应责任；我们将依法依约处理相关内容和账号权限。",
            "涉及人类遗传资源信息的，用户应确保已完成科技部事先报告和信息备份，并遵守《人类遗传资源管理条例》相关规定。"
          ]
        }
      ]
    },
    {
      heading: "五、用户内容权利与平台必要使用授权",
      blocks: [
        { type: "paragraph", text: "用户对其合法上传的资料、笔记、草稿、实验记录和课题组资料继续享有相应权利。除非另有明确约定，用户上传内容不会因使用 Truth-Seeker 而转让给我们。" },
        { type: "paragraph", text: "为实现用户主动发起的检索、阅读、生成、核验、同步、共享、备份、故障排查和安全审计等服务，用户授予我们在必要范围内处理、存储、展示、调用、分析和传输相关内容的非独占、可撤销、非转让授权。该授权以提供和保障服务为目的，不代表我们取得用户科研成果或知识产权所有权。" },
        { type: "paragraph", text: "用户私有资料不会默认用于训练公开模型或第三方模型；这一点也可概括为“不默认训练”。若后续需要将用户资料用于模型训练、算法优化、案例展示、市场宣传或对外发表，应另行取得用户或机构授权，或依据机构合同另行约定。" }
      ]
    },
    {
      heading: "六、课题组空间、共享和资料交接",
      blocks: [
        { type: "paragraph", text: "Truth-Seeker 可提供个人空间和课题组空间。个人空间默认仅用户本人可见；课题组空间按照 PI/导师、管理员、成员、访客等角色设置查看、上传、编辑、下载、分享、删除、成员管理和交接权限。" },
        { type: "paragraph", text: "学生毕业、成员离组、项目交接、合作结束或机构管理员调整权限时，应由课题组负责人、管理员或机构授权代表确认资料归属、可见范围和交接方式。我们可按授权指令提供权限变更、资料导出、转移或冻结等技术支持。" }
      ]
    },
    {
      heading: "七、第三方服务、模型调用与外部资源",
      blocks: [
        { type: "paragraph", text: "为完成文献检索、元数据补全、引用核验、摘要生成、AI 写作辅助、搜索增强或数据库访问，Truth-Seeker 可能调用大模型服务、开放学术数据源、搜索 API、机构授权数据库、文献数据库或其他第三方服务。" },
        { type: "paragraph", text: "我们会尽量按照最小必要原则传递完成当前任务所需的信息。涉及付费数据库、学校图书馆资源、机构 API 或第三方平台时，用户和机构仍应遵守对应服务条款、授权范围和版权限制，不得通过 Truth-Seeker 绕过付费墙、批量下载或违规分发版权全文。" }
      ]
    },
    {
      heading: "八、费用、套餐和试用",
      blocks: [
        { type: "paragraph", text: "Truth-Seeker 可提供免费试用、种子用户试用、机构试点、订阅套餐、私有化部署、仪器增值包或其他商业方案。具体价格、服务期限、功能范围、调用额度、支持方式、退款规则和发票信息，以产品页面、报价单、订单、机构合同或补充协议为准。" },
        { type: "paragraph", text: "试用服务可能存在功能限制、额度限制、数据保存期限限制或服务支持限制。试用结束后，如用户或机构未续费或未签署正式服务协议，我们可按照产品规则限制、暂停或终止相应服务，并按隐私政策和合同约定处理数据。" }
      ]
    },
    {
      heading: "九、知识产权与反馈",
      blocks: [
        { type: "paragraph", text: "Truth-Seeker 的产品名称、界面、程序、模型编排、Skill/Agent 配置、文档模板、系统设计、数据库结构、商标、著作权、专利、商业秘密和相关技术成果，除依法或另有约定属于第三方的部分外，归服务提供者或其关联方所有。" },
        { type: "paragraph", text: "用户在使用过程中提出的功能建议、问题反馈、测评意见和改进想法，我们可用于产品改进、质量评估和服务优化。涉及可识别用户、机构或敏感科研资料的对外案例展示，应另行取得必要授权。" }
      ]
    },
    {
      heading: "十、服务变更、中止与终止",
      blocks: [
        { type: "paragraph", text: "我们可根据研发进展、合规要求、第三方服务变化、系统安全、机构合同或商业安排，对功能、接口、模型、数据库、套餐、价格和服务方式进行调整，并通过产品公告、站内通知、邮件或合同约定方式告知。" },
        { type: "paragraph", text: "如用户违反法律法规、本协议、隐私政策、机构规则或第三方服务条款，或存在安全风险、侵权投诉、滥用接口、异常消耗资源等情形，我们可采取提醒、限制功能、暂停账号、删除违规内容、终止服务、保留证据或依法报告等措施。" }
      ]
    },
    {
      heading: "十一、责任限制",
      blocks: [
        { type: "paragraph", text: "在法律允许范围内，我们不对因用户输入错误、资料来源错误、第三方数据库限制、模型生成偏差、网络故障、机构授权问题、用户未核验 AI 输出或违反所在机构规则造成的损失承担超出法律和合同约定的责任。" },
        { type: "paragraph", text: "对于免费试用、测试版本、内测版本或实验性功能，我们将在合理范围内提供服务，但不承诺其在任何时间、任何场景均无错误、不中断或满足用户全部预期。" }
      ]
    },
    {
      heading: "十二、隐私保护与数据安全",
      blocks: [{ type: "paragraph", text: "我们将按照《Truth-Seeker 隐私政策》处理个人信息、科研资料、课题组资料、第三方调用数据和系统日志。用户应在注册和使用前阅读并同意该政策；机构版或私有化部署场景下，双方还可通过合同、数据处理协议或安全附件作进一步约定。" }]
    },
    {
      heading: "十三、法律适用、争议处理与协议更新",
      blocks: [
        { type: "paragraph", text: "本协议的订立、履行、解释和争议解决适用中华人民共和国法律。因本协议或 Truth-Seeker 服务产生争议的，双方应先友好协商；协商不成的，提交服务提供者住所地有管辖权的人民法院处理，机构合同另有约定的除外。" },
        { type: "paragraph", text: "我们可根据产品变化、法律法规变化、监管要求或运营需要更新本协议。重大变更将通过产品公告、站内通知、邮件或其他合理方式提示。用户继续使用服务，视为接受更新后的协议；如不同意更新，可停止使用并按隐私政策申请导出或删除相关数据。" }
      ]
    },
    {
      heading: "十四、联系我们",
      blocks: [{ type: "paragraph", text: "服务提供者：致真精密仪器（青岛）有限公司。统一社会信用代码：91370212MA3Q9KK07F。注册地址：山东省青岛市崂山区株洲路169号昌盛产业园E座2001。用户协议、账号、服务和争议相关问题可联系：0532-89267428（公司总机）。" }]
    }
  ]
};

export type LegalDocumentKind = "terms" | "privacy";
export type LegalDocumentVersion = "v1.0";

export const legalDocumentsByVersion: Record<
  LegalDocumentVersion,
  Record<LegalDocumentKind, LegalDocument>
> = {
  "v1.0": { terms: termsDocumentV1, privacy: privacyDocumentV1 }
};

export const currentLegalDocuments = legalDocumentsByVersion["v1.0"];
